import { useNavigate, generatePath } from 'react-router';
import { useMemo } from 'react';

import {
  Table,
  LoadingSpinner,
  Typography,
  useWindowSize,
  Chip,
} from '@data-products-and-ai/react-components';
import { TableRowData } from '@data-products-and-ai/react-components/lib/components/DataDisplay/Table/types';

import { Pagination, SemiCircleProgressBar } from '@/components';
import {
  OutagesFilters,
  OutagesList,
  RiskWarning,
} from '@/pages/Outages/components';
import {
  DESKTOP_LARGE_WIDTH,
  INITIAL_PAGE,
  INITIAL_PAGE_SIZE,
  INITIAL_SORT_DIRECTION,
  SPINNER_SIZE,
  TABLET_WIDTH,
} from '@/constants';
import {
  formatDateString,
  getRiskColor,
  getRiskLabel,
  toPascalCase,
} from '@/utils';
import { OutagesFiltersI } from '@/types';
import { usePagination, useTableSort } from '@/hooks';
import { useGetOutagesQuery } from '@/apis/outages/outagesApi';
import {
  useOutagesFilters,
  useOutagesTableHeaders,
} from '@/pages/Outages/hooks';
import { OUTAGE_ROUTE } from '@/pages/Outages/routes';
import { Outage } from '@/apis/outages/types/outagesApi';

import '@/pages/Outages/styles.scss';

export const OutagesPage = () => {
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isSmallerScreen = width <= TABLET_WIDTH;

  const { outagesFilters, updateFilters } = useOutagesFilters();
  const { currentPage, pageSize, setPage } = usePagination({
    initialPage: INITIAL_PAGE,
    initialPageSize: INITIAL_PAGE_SIZE,
  });
  const { sortConfig, requestSort } = useTableSort<Outage>(
    'outageStart',
    INITIAL_SORT_DIRECTION,
  );
  const orderBy = toPascalCase(sortConfig.key?.toString() ?? '');

  const { data, isLoading, isFetching } = useGetOutagesQuery({
    search: outagesFilters.query,
    endDate: outagesFilters.endDate,
    startDate: outagesFilters.startDate,
    siteId: Number(outagesFilters.siteId),
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    sortDir: sortConfig.direction,
    orderBy,
  });
  const outages = data?.results ?? [];
  const totalItems = data?.total ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  const headers = useOutagesTableHeaders(
    width <= DESKTOP_LARGE_WIDTH,
    sortConfig,
    requestSort,
  );

  const rows: TableRowData[] = useMemo(
    () =>
      (outages ?? []).map((outage) => ({
        id: outage.outageId.toString(),
        isClickable: true,
        data: {
          ...outage,
          outageStart: formatDateString(outage.outageStart, 'MM/DD/YYYY'),
          outageEnd: formatDateString(outage.outageEnd, 'MM/DD/YYYY'),
          workerCount: (
            <Chip
              label={outage.workerCount.toString()}
              chipBorderType="round"
              color="primary"
              chipType="thin"
              size="extra-small"
            />
          ),
          outageNormalized0_100: (
            <div className="outages-page__risk-indicator">
              <SemiCircleProgressBar
                progress={outage.outagePercentile}
                size={100}
                strokeWidth={8}
                color={getRiskColor(outage.outagePercentile)}
                labelText={getRiskLabel(outage.outagePercentile)}
                showLabel
                mode="label"
              />
              <RiskWarning
                highRiskCount={outage.highRiskCount ?? 0}
                midRiskCount={outage.midRiskCount ?? 0}
              />
            </div>
          ),
        },
      })),
    [outages],
  );

  const handleFiltersUpdate = (filters: Partial<OutagesFiltersI>) => {
    updateFilters(filters);
    setPage(INITIAL_PAGE);
  };

  const handleRowClick = (row: TableRowData) => {
    if (!row.id) return;

    navigate(generatePath(OUTAGE_ROUTE, { outageId: row.id }));
  };

  const handlePageChange = (page: number) => {
    setPage(page);

    if (isSmallerScreen) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (isLoading)
    return (
      <div className="outages-page-spinner">
        <LoadingSpinner
          text="Loading outages..."
          tag="textdefault"
          size={SPINNER_SIZE}
          color="var(--color-primary)"
          align="center"
          inline
        />
      </div>
    );

  if (!data) return null;

  return (
    <div className="outages-page">
      <div className="outages-page__title">
        <Typography
          color="var(--color-black)"
          tag={isSmallerScreen ? 'h3' : 'h2'}
        >
          Active Outages
        </Typography>
        <Typography color="var(--color-default)" tag="textdefault">
          {data?.total ?? 0}
        </Typography>
      </div>

      <OutagesFilters
        disabled={isFetching}
        outagesFilters={outagesFilters}
        onFiltersUpdate={handleFiltersUpdate}
      />

      <div className="outages-page__data">
        {isFetching && (
          <div className="outages-page__data-fetching">
            <LoadingSpinner
              text="Loading outages..."
              tag="textdefault"
              size={SPINNER_SIZE}
              color="var(--color-primary)"
              align="center"
              inline
            />
          </div>
        )}
        {isSmallerScreen ? (
          <OutagesList outages={outages ?? []} />
        ) : (
          <Table
            rows={rows}
            headers={headers}
            hasBottomBorder
            noResultsMessage="No outages found"
            pointerCursor
            onRowClick={handleRowClick}
            width="100%"
          />
        )}
      </div>

      <div className="outages-page__pagination">
        <Pagination
          disabled={isFetching}
          currentPage={currentPage}
          totalPages={totalPages}
          maxVisiblePages={isSmallerScreen ? 3 : 5}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};
